import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/gql';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import {
  Avatar,
  AvatarSelector,
} from 'components/rivalsUserGroup/AvatarSelector';

import {
  RivalsUserGroupAvatarEditQuery,
  RivalsUserGroupAvatarEditQueryVariables,
  UpdateUserGroupMutation,
  UpdateUserGroupMutationVariables,
} from './__generated__/index.graphql';

const StyledGridBackground = styled(GridBackground)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled(Vertical).attrs({ gap: 5 })`
  justify-content: space-between;
  height: 100%;
  @media ${tabletAndAbove} {
    justify-content: center;
    height: 400px;
  }
  padding: var(--quadruple-unit) 0;
  position: relative;
`;
const InnerWrapper = styled(Vertical)`
  position: absolute;
  width: 100%;
  top: 25%;
  padding: 0 var(--double-unit);
`;
const AVATAR_EDIT_QUERY = gql`
  query RivalsUserGroupAvatarEditQuery($after: String) {
    shopItems(
      types: COUNTRY_BADGE
      first: 20
      after: $after
      sortType: { direction: ASC, type: NAME }
    ) {
      nodes {
        ... on SkinShopItem {
          id
          pictureUrl
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
` as TypedDocumentNode<
  RivalsUserGroupAvatarEditQuery,
  RivalsUserGroupAvatarEditQueryVariables
>;

const UPDATE_USER_GROUP_AVATAR_MUTATION = gql`
  mutation UpdateUserGroupMutation($input: updateSo5UserGroupInput!) {
    updateSo5UserGroup(input: $input) {
      userGroup {
        slug
        logo {
          id
          pictureUrl
        }
      }
    }
  }
` as TypedDocumentNode<
  UpdateUserGroupMutation,
  UpdateUserGroupMutationVariables
>;

export const RivalsUserGroupAvatarEdit = ({
  userGroupId,
  onDone,
}: {
  userGroupId: string;
  onDone: () => void;
}) => {
  const { data: avatars, loadMore } = usePaginatedQuery(AVATAR_EDIT_QUERY, {
    connection: 'ClubShopItemConnection',
  });
  const { nodes, pageInfo } = avatars?.shopItems || {};
  const { hasNextPage, endCursor } = pageInfo || {};
  const [update, { loading }] = useMutation(UPDATE_USER_GROUP_AVATAR_MUTATION);

  const [changed, setChanged] = useState<boolean>();
  const [selected, setSelected] = useState<string>();

  return (
    <Wrapper>
      <StyledGridBackground
        height="100%"
        bgColor="var(--c-neutral-200)"
        color="#393939"
      >
        <InnerWrapper>
          <AvatarSelector
            avatars={
              (nodes || []).filter(node =>
                isType(node, 'SkinShopItem')
              ) as Avatar[]
            }
            onChange={({ id }) => {
              // hack to ignore first onChange which is not a user interaction
              // but comes from onVisibleItemsChanged
              if (changed === undefined) {
                setChanged(false);
                return;
              }
              setSelected(id);
              setChanged(true);
            }}
            onLoadMore={() => {
              if (hasNextPage) {
                loadMore(false, {
                  after: endCursor,
                });
              }
            }}
          />
        </InnerWrapper>
      </StyledGridBackground>
      <LoadingButton
        size="large"
        fullWidth
        loading={loading}
        disabled={!selected || !changed}
        onClick={async () => {
          await update({
            variables: {
              input: {
                so5UserGroupId: userGroupId,
                logoId: selected,
              },
            },
          });
          onDone();
        }}
      >
        <FormattedMessage
          id="RivalsUserGroupAvatarEdit.cta"
          defaultMessage="Update Avatar"
        />
      </LoadingButton>
    </Wrapper>
  );
};
